import Instance, { FrameData } from '../types/Instance';

interface editOptions {
  trimStart?: number;
  trimEnd?: number;
  includeInterval?: number;
}

export interface SelectedInstanceFrames {
  instance: Instance;
  frames: FrameData[];
}

/**
 * Select which frames of a series survive the trim/interval edits.
 *
 * Operates on the *flattened* frame list across all instances — the same
 * sequence the viewer scrolls through — so trimStart/trimEnd count frames, not
 * instances. This is what makes the tools work for multiframe instances (US
 * cine, enhanced CT/MR) where a single instance contains many frames.
 *
 * Returns the kept frames grouped back per instance, preserving instance and
 * frame order and omitting any instance with no surviving frames.
 *
 * NOTE: includeInterval is implemented but intentionally has no UI control. It
 * produces non-contiguous frame subsets, which is harmless for temporal
 * multiframe (US) but changes slice spacing / dimension indices on enhanced
 * geometric volumes (CT/MR) and can yield odd-looking DICOM elsewhere. Left
 * wired up so it can be surfaced later if users ask for it.
 */
export default function selectFrames(
  instanceArray: Instance[],
  options: editOptions
): SelectedInstanceFrames[] {
  // Flatten to a single ordered list, matching the viewer's flatMap order.
  let flat = instanceArray.flatMap((instance) =>
    instance.frames.map((frame) => ({ instance, frame }))
  );

  if (options.trimStart) {
    flat = flat.slice(options.trimStart);
  }
  if (options.trimEnd) {
    flat = flat.slice(0, flat.length - options.trimEnd);
  }
  if (options.includeInterval && options.includeInterval > 1) {
    flat = flat.filter((_, i) => i % options.includeInterval! === 0);
  }

  // Group kept frames back per instance, preserving order and dropping
  // instances that have no surviving frames.
  const grouped: SelectedInstanceFrames[] = [];
  const indexByUid = new Map<string, number>();
  for (const { instance, frame } of flat) {
    let idx = indexByUid.get(instance.uid);
    if (idx === undefined) {
      idx = grouped.length;
      indexByUid.set(instance.uid, idx);
      grouped.push({ instance, frames: [] });
    }
    grouped[idx].frames.push(frame);
  }
  return grouped;
}
